// Đoán giờ sinh: trang này chỉ là bìa — bấm vào là mở luồng trắc nghiệm TOÀN MÀN
// HÌNH (`BirthHourQuizOverlay`), khai ngày sinh + giới tính rồi trả lời từng câu
// một. Hết câu cố định thì sang câu do LLM soạn, xong mới ra kết quả.
//
// Hook giữ state nằm ở ĐÂY, không nằm trong overlay: đóng giữa đường rồi mở lại
// thì người dùng quay về đúng chỗ đang dở thay vì làm lại từ câu 1.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Clock, ListChecks, Loader2, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";

import { BirthHourQuizOverlay } from "@/components/doan-gio/birth-hour-quiz";
import { BirthHourHistory } from "@/components/doan-gio/birth-hour-history";
import { useBirthHourQuiz } from "@/components/doan-gio/use-birth-hour-quiz";
import { displayName, useBoideUser } from "@/hooks/use-boide-user";
import { getBirthTimeHistory } from "@/lib/boide-api";

export const Route = createFileRoute("/doan-gio-sinh")({
  // `?probe=` là đường vào từ trang Lịch sử chung — mở thẳng một lượt đã chấm.
  //
  // ⚠️ Phải là TUỲ CHỌN: khai bắt buộc thì mọi `<Link to="/doan-gio-sinh">` sẵn có
  // (nav, trang chủ, ô giờ sinh ở /tu-vi và /bat-tu) đều thành lỗi biên dịch.
  validateSearch: (search: Record<string, unknown>): { probe?: string } => ({
    probe: typeof search.probe === "string" && search.probe ? search.probe : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Đoán giờ sinh qua trắc nghiệm - Đối chiếu 13 lá số Tử Vi & Bát Tự | Bói Đê" },
      {
        name: "description",
        content:
          "Không nhớ giờ sinh? Trả lời vài câu trắc nghiệm, Bói Đê lập 13 lá số Tử Vi và 13 lá số Bát Tự rồi chấm giờ nào khớp với cuộc đời bạn nhất.",
      },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/doan-gio-sinh" }],
  }),
  component: DoanGioSinhPage,
});

const STEPS = [
  {
    icon: Clock,
    title: "Khai ngày sinh",
    text: "Ngày, tháng, năm và giới tính. Không cần giờ — đó là thứ sẽ được đoán ra.",
  },
  {
    icon: ListChecks,
    title: "Trả lời từng câu",
    text: "Mỗi câu một màn hình, chọn xong tự sang câu sau. Không chắc thì bỏ qua.",
  },
  {
    icon: Target,
    title: "Câu hỏi riêng cho bạn",
    text: "Hết câu chung, hệ thống soạn thêm vài câu bám đúng chỗ các giờ còn lại khác nhau.",
  },
  {
    icon: Sparkles,
    title: "Nhận kết quả",
    text: "Khung giờ khớp nhất, độ tin cậy, và độ khớp của cả 13 khung giờ.",
  },
];

function DoanGioSinhPage() {
  const [open, setOpen] = useState(false);
  const quiz = useBirthHourQuiz();
  const { user } = useBoideUser();
  const navigate = useNavigate({ from: "/doan-gio-sinh" });
  const { probe } = Route.useSearch();

  /**
   * Mở một lượt cũ khi vào bằng `?probe=` (từ trang Lịch sử chung).
   *
   * ⚠️ `handled` chặn mở lại: người xem đóng lớp trắc nghiệm xong thì param đã được gỡ
   * khỏi URL, nhưng effect vẫn chạy lại theo mọi lần render khác — thiếu chốt này là
   * đóng xong nó tự bật lên lần nữa. Gỡ param NGAY khi nhận việc, không đợi kết quả:
   * giữ nó lại là tải lại trang sau khi đã đóng sẽ mở ra y như cũ, tức URL nói một
   * đằng màn hình một nẻo.
   */
  const handled = useRef<string | null>(null);
  const openFromHistory = useMutation({
    mutationFn: getBirthTimeHistory,
    onSuccess: (data) => {
      quiz.openHistoryResult(data);
      setOpen(true);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Không mở được lượt đoán này"),
  });
  const { mutate: loadProbe } = openFromHistory;
  useEffect(() => {
    if (!probe || handled.current === probe) return;
    handled.current = probe;
    void navigate({ search: { probe: undefined }, replace: true });
    loadProbe(probe);
  }, [probe, navigate, loadProbe]);

  const person = {
    name: quiz.form.hoTen.trim() || displayName(user, "") || "Người xem",
    gender: quiz.form.gender,
  };

  return (
    <main className="birth-hour-page min-h-0 flex-1 overflow-y-auto pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:pb-14">
      <div className="mx-auto max-w-[760px] px-4 py-5 sm:px-6 md:py-8">
        <h1 className="sr-only">Đoán giờ sinh qua trắc nghiệm</h1>

        {/* Vào từ trang Lịch sử thì có một nhịp chờ gọi máy chủ trước khi lớp trắc
            nghiệm bật lên — nói ra, không thì trang trông như bấm hụt. */}
        {openFromHistory.isPending && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 flex items-center gap-2 rounded-2xl border border-gold-soft/45 bg-card/60 px-4 py-3 text-xs text-muted-foreground"
          >
            <Loader2 className="size-4 animate-spin text-primary" />
            Đang mở lượt đoán từ lịch sử…
          </div>
        )}

        {/* Cả khối là một cái nút — "ấn vào mở full màn hình". */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="birth-hour-cover group relative w-full cursor-pointer overflow-hidden rounded-[28px] border px-6 pb-10 pt-8 text-center transition hover:border-primary/60 sm:px-10"
        >
          <span className="relative flex flex-col items-center gap-[18px]">
            <span className="birth-hour-cover__eyebrow">MỖI CUỘC ĐỜI ĐỀU ĐỂ LẠI DẤU VẾT</span>
            <img
              src="/ritual/birth-hour-emblem.webp"
              alt=""
              className="size-[106px] object-contain"
            />
            <span className="block">
              <span className="birth-hour-cover__title block font-serif-soft text-[34px] font-semibold leading-[44px]">
                Tìm lại
                <br />
                giờ sinh của bạn
              </span>
              <span className="birth-hour-cover__text mx-auto mt-3 block max-w-[350px] text-[13px] leading-[23px]">
                Từ tính cách đến những bước ngoặt đã qua, từng câu trả lời sẽ hé mở khung giờ gắn
                với cuộc đời bạn.
              </span>
            </span>
            <span className="birth-hour-cover__button inline-flex min-h-[52px] items-center gap-2.5 rounded-full border px-6 text-[13px] font-semibold transition group-hover:scale-[1.02]">
              <Sparkles className="size-4" />
              {quiz.stage === "birth" ? "Bắt đầu khám phá" : "Tiếp tục hành trình"}
            </span>
            <span className="birth-hour-cover__note text-[10px] tracking-[0.4px]">
              Đoán giờ sinh · Miễn phí · 2–3 phút
            </span>
          </span>
        </button>

        <section className="mt-6">
          <h2 className="text-sm font-bold text-foreground">Cách hoạt động</h2>
          <ol className="mt-3 grid gap-3 sm:grid-cols-2">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-2xl border border-border/60 bg-card/60 p-4">
                <div className="flex items-center gap-2">
                  <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <s.icon className="size-4" />
                  </span>
                  <h3 className="text-sm font-semibold text-foreground">
                    {i + 1}. {s.title}
                  </h3>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <BirthHourHistory
          enabled={quiz.isAuthenticated}
          onOpen={(data) => {
            quiz.openHistoryResult(data);
            setOpen(true);
          }}
        />

        <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground">
          Đoán giờ sinh <strong className="text-foreground">miễn phí</strong>, không giới hạn số
          lượt và không trừ số dư. Cần đăng nhập để lượt đoán được lưu vào tài khoản — mở bài rồi bỏ
          dở thì lần sau quay lại đúng lượt đó.
        </p>

        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          Kết quả là một{" "}
          <strong className="text-foreground">khoảng giờ có khả năng cao nhất</strong>, không phải
          một con số chắc chắn. Hai khung giờ liền kề rất dễ hòa nhau — hệ thống nói rõ chỗ chưa
          chắc thay vì giấu sau một thứ hạng.
        </p>
      </div>

      {/* Không gắn `SiteFooter` ở đây: đây là trang tính năng, xếp cùng nhóm với
          /tu-vi, /bat-tu, /kinh-dich — chân trang chỉ nằm ở trang chủ và các trang
          điều hướng (/chu-de, /van-khi). */}
      <BirthHourQuizOverlay quiz={quiz} person={person} open={open} onOpenChange={setOpen} />
    </main>
  );
}
